import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import { loadRazorpay, RAZORPAY_THEME_COLOR, getCheckoutImage, type RazorpayResponse } from '@/lib/razorpay';
import type { Plan, Subscription, UpgradePreview } from './types';
import { formatDate, formatMoney } from './dateFormat';
import { UpgradeModal } from './UpgradeModal';
import { CYCLE_LABELS, availableCycles, initialCycle, plansForCycle, type BillingCycle } from './cycles';
import { getDocsUrl } from '../SettingsModal';

/** A coupon the user is eligible to see on this page (GET /coupons/offers). */
interface Offer {
  code: string;
  description: string | null;
  discountType: 'PERCENT' | 'FIXED';
  discountValue: string;
  currency: string;
  scope: string;
}
interface CouponResult { finalAmount: number; discountAmount: number; currency: string }
type ValidateResp =
  | { valid: false; reason: string }
  | { valid: true; originalAmount: number; discountAmount: number; finalAmount: number; currency: string };

/**
 * In-modal plan picker for the Billing tab.
 *
 *  - UPGRADE (higher-priced plan): opens Razorpay Checkout for the PRORATED
 *    difference (newPrice − unused credit of the current plan). The card shows
 *    that credit + amount due up front via /subscriptions/upgrade-preview. A
 *    paid→paid upgrade keeps the original renewal date (server-side).
 *  - DOWNGRADE to Free: never immediate — scheduled for the end of the current
 *    period so the user keeps their paid allowance until then. A pending
 *    downgrade shows a banner with a "Cancel downgrade" action.
 *  - DOWNGRADE to a lower PAID plan: not offered (would need a fresh payment at
 *    activation); the card is disabled with a hint to use Free or wait.
 *
 * Paid flows open the Razorpay Checkout modal against a freshly-created order;
 * its success handler POSTs the signed handshake to /payments/callback. The
 * plan activates from that callback (or the authoritative webhook); if the
 * callback comes back PENDING, this view polls /payments/confirm as a fallback.
 * Reuses the .settings-* card styling.
 */

interface BillingPlansViewProps {
  /** The caller's active subscription (drives current/upgrade/downgrade state). */
  subscription: Subscription | null;
  /** Return to the billing overview. */
  onBack(): void;
  /** Called after a plan change so the billing cards refetch. */
  onChanged(): void;
}

type Relation = 'current' | 'upgrade' | 'downgrade-free' | 'downgrade-paid' | 'unavailable';

/**
 * User-facing allowance. The MONTHLY pool leads, because that is the number the
 * plan actually grants and exactly what the Usage screen meters against; the
 * daily figure follows as what it really is — a fair-use burst limit worth a
 * tenth of that pool (see DAILY_SHARE in the plan seed).
 *
 * The card used to headline the DAILY cap alone, which read as a sustained rate:
 * "230 Assist credits / day" implied 6,900 a month on a plan that grants 4,000.
 * Leading with the month and labelling the day as fair-use leaves one checkable
 * sum — daily = monthly / 10 — instead of two numbers that disagree.
 *
 * BOTH budgets always appear (every paid plan grants Assist AND BrewwCode; the
 * card previously showed only the group's own one, hiding half the plan). The
 * plan's group just decides which leads. Tokens are the internal metering unit —
 * only shown for legacy token-only plans.
 */
function allowanceLines(
  plan: Pick<Plan, 'monthlyCredits' | 'brewcodeMonthlyCredits' | 'dailyCreditCap' | 'brewcodeDailyCreditCap' | 'planGroup' | 'tokenLimit'>,
): { lines: string[]; fairUse: string | null } {
  const n = (v: number): string => v.toLocaleString();

  if (plan.monthlyCredits == null) {
    const legacy = plan.tokenLimit == null ? 'Unlimited' : `${n(plan.tokenLimit)} tokens / 30 days`;
    return { lines: [legacy], fairUse: null };
  }

  const assist = `${n(plan.monthlyCredits)} Assist credits / month`;
  const hasBrewcode = plan.brewcodeMonthlyCredits != null && plan.brewcodeMonthlyCredits > 0;
  const brewcode = hasBrewcode
    ? `${n(plan.brewcodeMonthlyCredits!)} BrewwCode credits / month`
    : 'BrewwCode not included';
  const lines = plan.planGroup === 'developer' ? [brewcode, assist] : [assist, brewcode];

  // Spell out the burst limit in credits AND as a share of the pool, so the daily
  // number is verifiable from the monthly one on the card itself. The share is
  // COMPUTED from the two figures rather than asserted: an operator can override
  // a cap in the admin plan form, and a card must never claim a ratio the plan
  // doesn't actually have (that claim is the whole bug being fixed here).
  const daily: string[] = [];
  const shares: number[] = [];
  if (plan.dailyCreditCap != null) {
    daily.push(`${n(plan.dailyCreditCap)} Assist`);
    shares.push(Math.round((plan.dailyCreditCap / plan.monthlyCredits) * 100));
  }
  if (hasBrewcode && plan.brewcodeDailyCreditCap != null) {
    daily.push(`${n(plan.brewcodeDailyCreditCap)} BrewwCode`);
    shares.push(Math.round((plan.brewcodeDailyCreditCap / plan.brewcodeMonthlyCredits!) * 100));
  }
  if (daily.length === 0) return { lines, fairUse: null };

  // Only name a percentage when every budget shares it; otherwise the numbers
  // stand on their own.
  const uniform = shares.length > 0 && shares.every((s) => s === shares[0]);
  const suffix = uniform
    ? ` — ${shares[0]}% of ${daily.length > 1 ? 'each budget' : 'the month'}`
    : '';
  return { lines, fairUse: `Fair use: up to ${daily.join(' + ')} per day${suffix}` };
}

/** Both numbers come straight from the DB: `price` is what checkout charges,
 *  `basePrice` is the list price to strike through. No arithmetic here — the
 *  server computed `price` once, on save. */
function priceLabel(plan: Plan): { price: string; original: string | null } {
  const price = Number(plan.price);
  if (!Number.isFinite(price) || price === 0) return { price: 'Free', original: null };
  const base = Number(plan.basePrice ?? plan.price);
  const discounted = Number.isFinite(base) && base > price;
  const CYCLE_UNIT: Record<string, string> = { weekly: 'week', monthly: 'month', yearly: 'year' };
  const cycle = plan.billingCycle.toLowerCase();
  return {
    price: `${formatMoney(price, plan.currency)} / ${CYCLE_UNIT[cycle] ?? cycle}`,
    original: discounted ? formatMoney(base, plan.currency) : null,
  };
}

function relationOf(plan: Plan, currentPlanId: string | null, currentPrice: number): Relation {
  if (plan.id === currentPlanId) return 'current';
  const price = Number(plan.price);
  if (price > currentPrice) return 'upgrade';
  if (price < currentPrice) return price === 0 ? 'downgrade-free' : 'downgrade-paid';
  return 'unavailable';
}

export function BillingPlansView({ subscription, onBack, onChanged }: BillingPlansViewProps) {
  const currentPlanId = subscription?.plan?.id ?? null;
  const currentPrice = Number(subscription?.plan?.price ?? 0);
  const periodEnd = subscription?.endDate ?? null;
  const scheduledDowngrade = subscription?.scheduledDowngrade ?? null;

  const [plans, setPlans] = useState<Plan[]>([]);
  // Billing cycle being browsed. null until the plans load decides the opening
  // tab (see initialCycle); a refetch never clobbers a choice already made.
  const [cycle, setCycle] = useState<BillingCycle | null>(null);
  const [previews, setPreviews] = useState<Record<string, UpgradePreview>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [selecting, setSelecting] = useState<string | null>(null);
  // The Free plan awaiting an explicit downgrade confirmation, or null.
  const [confirmDowngrade, setConfirmDowngrade] = useState<Plan | null>(null);
  // The plan whose upgrade confirmation modal is open, or null.
  const [upgradePlan, setUpgradePlan] = useState<Plan | null>(null);
  const [acting, setActing] = useState(false);

  // Offers / coupons
  const [offers, setOffers] = useState<Offer[]>([]);
  const [couponInput, setCouponInput] = useState('');
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  const [couponResults, setCouponResults] = useState<Record<string, CouponResult>>({});
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponBusy, setCouponBusy] = useState(false);

  // A paid order awaiting confirmation via the /payments/confirm poll (armed
  // only when the Checkout callback came back PENDING — the webhook is still
  // catching up). Non-null shows the "confirming" banner and runs the poll.
  const [pending, setPending] = useState<{ plan: Plan; orderId: string } | null>(null);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  // Read inside load() without widening its deps (same trick as onChangedRef).
  const subscriptionPlanRef = useRef<Plan | null>(subscription?.plan ?? null);
  subscriptionPlanRef.current = subscription?.plan ?? null;

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ plans: Plan[] }>('/api/v2/plans');
      const list = res.plans ?? [];
      setPlans(list);
      // Open on the cycle the caller is already billed on, but leave a choice
      // they've already made alone (this refetches after every plan change).
      setCycle((prev) => prev ?? initialCycle(list, subscriptionPlanRef.current));
      // Offers this user is eligible to see — best-effort, never blocks the page.
      try {
        const off = await api.get<{ offers: Offer[] }>('/api/v2/coupons/offers');
        setOffers(off.offers ?? []);
      } catch { /* ignore */ }
      // Prefetch prorated previews for genuine upgrades (only when we have a
      // priced current plan — a free→paid jump has no credit to show).
      if (currentPrice > 0) {
        const upgrades = list.filter((p) => Number(p.price) > currentPrice);
        const results = await Promise.all(
          upgrades.map((p) =>
            api
              .get<{ preview: UpgradePreview }>(`/api/v2/subscriptions/upgrade-preview?planId=${encodeURIComponent(p.id)}`)
              .then((r) => [p.id, r.preview] as const)
              .catch(() => null),
          ),
        );
        const map: Record<string, UpgradePreview> = {};
        for (const entry of results) if (entry) map[entry[0]] = entry[1];
        setPreviews(map);
      } else {
        setPreviews({});
      }
    } catch (e) {
      setError((e as Error).message || 'Could not load plans');
    } finally {
      setLoading(false);
    }
  }, [currentPrice]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Payment fully confirmed — clear busy state, flash success, refetch. */
  const settlePaid = useCallback((plan: Plan): void => {
    setPending(null);
    setSelecting(null);
    setFlash(`You're now on the ${plan.name} plan.`);
    onChangedRef.current();
  }, []);

  // Fallback poll: when the Checkout callback returned PENDING (the async
  // webhook hasn't landed yet, e.g. Brew on localhost), keep asking
  // /payments/confirm — which pulls the order's true status from Razorpay and
  // activates the plan on PAID — until it settles or times out.
  useEffect(() => {
    if (!pending) return;
    const { plan, orderId } = pending;
    let stopped = false;
    const startedAt = Date.now();
    const MAX_MS = 5 * 60_000;

    const poll = async (): Promise<void> => {
      if (stopped) return;
      if (Date.now() - startedAt > MAX_MS) {
        stopped = true;
        setPending(null);
        setSelecting(null);
        setFlash('Your payment is processing — it will reflect on this page shortly.');
        return;
      }
      try {
        const r = await api.post<{ status: 'PENDING' | 'PAID' | 'FAILED' }>(
          '/api/v2/payments/confirm', { orderId },
        );
        if (r.status === 'PAID') { stopped = true; settlePaid(plan); return; }
        if (r.status === 'FAILED') {
          stopped = true;
          setPending(null);
          setSelecting(null);
          setError('Payment was not completed. You can try again.');
          return;
        }
      } catch {
        /* transient (Razorpay slow / network) — keep polling */
      }
    };

    void poll();
    const id = window.setInterval(() => void poll(), 2000);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [pending, settlePaid]);

  const cancelWaiting = (): void => {
    setPending(null);
    setSelecting(null);
    setFlash(null);
  };

  /** Send the signed Checkout handshake to the backend to verify + finalize. */
  const finalizeCheckout = useCallback(async (plan: Plan, orderId: string, resp: RazorpayResponse): Promise<void> => {
    setFlash('Confirming your payment…');
    try {
      const r = await api.post<{ status: 'PENDING' | 'PAID' | 'FAILED' }>('/api/v2/payments/callback', {
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
      });
      if (r.status === 'PAID') { settlePaid(plan); return; }
      if (r.status === 'FAILED') {
        setSelecting(null);
        setError('Payment was not completed. You can try again.');
        return;
      }
      // PENDING → the payment succeeded but capture hasn't reflected yet; the
      // authoritative webhook will finalize. Arm the confirm poll meanwhile.
      setPending({ plan, orderId });
    } catch {
      // The callback POST failed, but the webhook is authoritative — poll confirm.
      setPending({ plan, orderId });
    }
  }, [settlePaid]);

  /** Validate a code against every paid plan so eligible cards show the discount. */
  async function applyCoupon(rawCode: string): Promise<void> {
    const code = rawCode.trim();
    if (!code) return;
    setCouponBusy(true);
    setCouponError(null);
    try {
      const paidPlans = plans.filter((p) => Number(p.price) > 0);
      const results: Record<string, CouponResult> = {};
      await Promise.all(
        paidPlans.map(async (p) => {
          try {
            const r = await api.get<ValidateResp>(
              `/api/v2/coupons/validate?code=${encodeURIComponent(code)}&planId=${encodeURIComponent(p.id)}`,
            );
            if (r.valid) results[p.id] = { finalAmount: r.finalAmount, discountAmount: r.discountAmount, currency: r.currency };
          } catch { /* per-plan failure is non-fatal */ }
        }),
      );
      if (Object.keys(results).length === 0) {
        setCouponError("This code isn't valid for any plan you can buy right now.");
        setAppliedCode(null);
        setCouponResults({});
      } else {
        setAppliedCode(code.toUpperCase());
        setCouponResults(results);
        setCouponInput(code.toUpperCase());
      }
    } finally {
      setCouponBusy(false);
    }
  }
  function clearCoupon(): void {
    setAppliedCode(null);
    setCouponResults({});
    setCouponError(null);
    setCouponInput('');
  }

  /** Paid upgrade (or free→paid): create the order and open Razorpay Checkout. */
  async function startCheckout(plan: Plan): Promise<void> {
    setSelecting(plan.id);
    setFlash(null);
    setError(null);
    try {
      const upgrade = currentPlanId != null && currentPlanId !== plan.id;
      const useCoupon = appliedCode && couponResults[plan.id] ? appliedCode : undefined;
      const order = await api.post<{ orderId: string; keyId: string; amount: number; currency: string; activated?: boolean }>(
        `/api/v2/payments/initiate${upgrade ? '?upgrade=true' : ''}`,
        { planId: plan.id, ...(useCoupon ? { couponCode: useCoupon } : {}) },
      );
      // 100%-off coupon: the server activated directly, no Razorpay step.
      if (order.activated) {
        clearCoupon();
        settlePaid(plan);
        return;
      }
      const Razorpay = await loadRazorpay();
      let handled = false;
      const rzp = new Razorpay({
        key: order.keyId,
        order_id: order.orderId,
        amount: order.amount,
        currency: order.currency,
        name: 'BREWW AI',
        description: 'Product of Datacaffe.ai',
        image: getCheckoutImage(),
        theme: { color: RAZORPAY_THEME_COLOR },
        handler: (resp) => {
          handled = true;
          void finalizeCheckout(plan, order.orderId, resp);
        },
        modal: {
          ondismiss: () => {
            // Dismissed without completing payment (the handler fires first when
            // paid, so `handled` guards the paid-then-closed race).
            if (handled) return;
            setSelecting(null);
            setFlash('Checkout was closed before payment completed.');
          },
        },
      });
      rzp.open();
    } catch (e) {
      setError((e as Error).message || 'Could not start checkout');
      setSelecting(null);
    }
  }

  /** Schedule a downgrade to Free for the end of the current billing period. */
  async function scheduleDowngrade(plan: Plan): Promise<void> {
    setActing(true);
    setFlash(null);
    setError(null);
    try {
      await api.post('/api/v2/subscriptions/schedule-downgrade', { planId: plan.id });
      setConfirmDowngrade(null);
      setFlash(`Downgrade to ${plan.name} scheduled for ${formatDate(periodEnd)}. You keep your current plan until then.`);
      onChanged();
    } catch (e) {
      setError((e as Error).message || 'Could not schedule the downgrade');
    } finally {
      setActing(false);
    }
  }

  /** Cancel a pending scheduled downgrade so the current plan simply renews. */
  async function cancelDowngrade(): Promise<void> {
    setActing(true);
    setFlash(null);
    setError(null);
    try {
      await api.post('/api/v2/subscriptions/cancel-downgrade', {});
      setFlash('Scheduled downgrade cancelled — your current plan will renew as usual.');
      onChanged();
    } catch (e) {
      setError((e as Error).message || 'Could not cancel the downgrade');
    } finally {
      setActing(false);
    }
  }

  const busyAny = selecting != null || pending != null || acting;

  // Cycle segmentation: the pill only offers cycles that have a paid plan, and
  // hides below two so the picker can never show an empty grid. The current plan
  // is pinned into every tab so the "Current" card never disappears.
  const cycles = availableCycles(plans);
  const activeCycle = cycle ?? initialCycle(plans, subscription?.plan ?? null);
  const visiblePlans = plansForCycle(plans, activeCycle, currentPlanId);

  // The applied coupon (if any) for the plan whose Upgrade modal is open.
  const upgradeRes = upgradePlan ? couponResults[upgradePlan.id] : undefined;
  const upgradeCoupon = upgradePlan && appliedCode && upgradeRes
    ? { code: appliedCode, discountAmount: upgradeRes.discountAmount, finalAmount: upgradeRes.finalAmount }
    : null;

  return (
    <div className="billing-plans-view">
      <button type="button" className="settings-back" onClick={onBack}>
        ← Back
      </button>

      {scheduledDowngrade && (
        <div className="settings-notice billing-downgrade-banner">
          Your plan downgrades to <strong>{scheduledDowngrade.planName || 'Free'}</strong> on{' '}
          <strong>{formatDate(scheduledDowngrade.effectiveDate ?? periodEnd)}</strong>. You keep your current
          plan until then.{' '}
          <button
            type="button"
            disabled={busyAny}
            onClick={() => void cancelDowngrade()}
            style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer', color: 'inherit', textDecoration: 'underline' }}
          >
            Cancel downgrade
          </button>
        </div>
      )}

      {flash && <div className="settings-flash">{flash}</div>}
      {error && <div className="settings-error">{error}</div>}

      {!loading && !error && offers.length > 0 && (
        <div className="settings-offers">
          <div className="settings-offers__head">🎉 Offers available for you</div>
          {offers.map((o) => {
            const applied = appliedCode === o.code;
            return (
              <div key={o.code} className="settings-offer">
                <span className="settings-offer__badge">
                  {o.discountType === 'PERCENT'
                    ? `${Number(o.discountValue)}% OFF`
                    : `${o.currency} ${Number(o.discountValue)} OFF`}
                </span>
                <div className="settings-offer__body">
                  <div className="settings-offer__desc">
                    {o.description || (o.scope === 'NEW_USERS' ? 'New-customer offer' : 'Special offer')}
                  </div>
                  {/* Internal auto-minted BREW-xxxx codes are never shown/typed by
                      users (they apply via the button) — matches CheckoutCard. */}
                  {!/^BREW-[A-Z0-9]+$/i.test(o.code) && <div className="settings-offer__code">code {o.code}</div>}
                </div>
                <button
                  type="button"
                  className={`settings-offer__btn${applied ? ' is-applied' : ''}`}
                  disabled={couponBusy || applied}
                  onClick={() => void applyCoupon(o.code)}
                >
                  {applied ? '✓ Applied' : 'Apply'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && (
        <div className="settings-coupon">
          {appliedCode ? (
            <div className="settings-coupon__applied">
              <span className="settings-coupon__check">✓</span>
              <span className="settings-coupon__code">{/^BREW-[A-Z0-9]+$/i.test(appliedCode) ? 'Offer' : appliedCode}</span>
              <span className="settings-coupon__note">applied — discounted prices shown below</span>
              <button type="button" className="settings-coupon__remove" onClick={clearCoupon}>Remove</button>
            </div>
          ) : (
            <>
              <div className="settings-coupon__row">
                <input
                  type="text"
                  placeholder="Have a coupon code?"
                  value={couponInput}
                  disabled={couponBusy}
                  onChange={(e) => setCouponInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void applyCoupon(couponInput); }}
                  className="settings-coupon-input"
                />
                <button
                  type="button"
                  className="settings-btn settings-btn--primary settings-coupon-btn"
                  disabled={couponBusy || !couponInput.trim()}
                  onClick={() => void applyCoupon(couponInput)}
                >
                  {couponBusy ? 'Checking…' : 'Apply'}
                </button>
              </div>
              {couponError && <div className="settings-coupon__error">{couponError}</div>}
            </>
          )}
        </div>
      )}

      {pending && (
        <div className="settings-notice">
          Confirming your payment to <strong>{pending.plan.name}</strong>… this page updates
          automatically once it’s done.{' '}
          <button
            type="button"
            onClick={cancelWaiting}
            style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer', color: 'inherit', textDecoration: 'underline' }}
          >
            Stop waiting
          </button>
        </div>
      )}
      {loading && !error && <p className="settings-muted">Loading plans…</p>}

      {!loading && !error && cycles.length > 1 && (
        <div className="billing-cycles" role="group" aria-label="Billing cycle">
          <div className="billing-cycles__track">
            {cycles.map((c) => (
              <button
                key={c}
                type="button"
                className={`billing-cycle${c === activeCycle ? ' is-active' : ''}`}
                aria-pressed={c === activeCycle}
                onClick={() => setCycle(c)}
              >
                {CYCLE_LABELS[c]}
              </button>
            ))}
          </div>
        </div>
      )}

      {!loading && !error && (() => {
        // Two sections: General (Assist-first) then Developer (BrewwCode-first).
        // When only one group is present (e.g. after cycle filtering) the headers
        // hide and it renders as a single grid — identical to the old flat layout.
        const groupOf = (p: Plan) => (p.planGroup === 'developer' ? 'developer' : 'general');
        const generalPlans = visiblePlans.filter((p) => groupOf(p) === 'general');
        const developerPlans = visiblePlans.filter((p) => groupOf(p) === 'developer');
        const bothGroups = generalPlans.length > 0 && developerPlans.length > 0;
        const groupTitle = (t: string) => (
          <div style={{ fontWeight: 700, fontSize: '0.9rem', margin: '16px 2px 8px', opacity: 0.8 }}>{t}</div>
        );
        const renderPlanCard = (plan: Plan) => {
            const relation = relationOf(plan, currentPlanId, currentPrice);
            const preview = previews[plan.id];
            const busy = selecting === plan.id;
            const disc = appliedCode ? couponResults[plan.id] : undefined;
            return (
              <div className={`settings-card${relation === 'current' ? ' is-current' : ''}`} key={plan.id}>
                {/* No tier badge here — the plan name already says Power/Pro,
                    so "POWER" next to it was noise. `plan.tier` still drives
                    entitlements server-side; this is presentation only. */}
                <div className="settings-card__head">
                  <span className="settings-card__name">
                    {plan.tierLabel && (
                      <span style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.6 }}>
                        {plan.tierLabel}
                      </span>
                    )}
                    {plan.name}
                  </span>
                </div>
                {(() => {
                  const { price, original } = priceLabel(plan);
                  const rawCyc = plan.billingCycle.toLowerCase();
                  const cyc = ({ weekly: 'week', monthly: 'month', yearly: 'year' } as Record<string, string>)[rawCyc] ?? rawCyc;
                  if (disc) {
                    // Offer applied → strike the list price, highlight the discounted pay price.
                    return (
                      <>
                        <div className="settings-card__was">
                          <s className="settings-card__strike">{price}</s>
                        </div>
                        <div className="settings-card__price settings-card__price--offer">
                          {formatMoney(disc.finalAmount, disc.currency)} / {cyc}
                        </div>
                        <div className="settings-card__save-line">
                          {appliedCode} · save {formatMoney(disc.discountAmount, disc.currency)}
                        </div>
                      </>
                    );
                  }
                  return (
                    <>
                      {original && (
                        <div className="settings-card__was">
                          <s className="settings-card__strike">{original}</s>
                        </div>
                      )}
                      <div className="settings-card__price">{price}</div>
                    </>
                  );
                })()}
                {(() => {
                  const { lines, fairUse } = allowanceLines(plan);
                  return (
                    <div className="settings-card__allowance">
                      {lines.map((line) => (
                        <div className="settings-card__allowance-line" key={line}>{line}</div>
                      ))}
                      {fairUse && <div className="settings-card__fairuse">{fairUse}</div>}
                    </div>
                  );
                })()}

                {relation === 'upgrade' && preview && preview.credit > 0 && (() => {
                  // Fold the applied coupon INTO the prorated amount — same as
                  // UpgradeModal + the backend (discount-then-prorate) — so the
                  // card, the modal and the actual charge all agree. Without this
                  // the card showed the pre-coupon amount (credit only).
                  const couponDiscount = disc ? Math.min(disc.discountAmount, preview.amountDue) : 0;
                  const dueToday = Math.max(0, Math.round((preview.amountDue - couponDiscount) * 100) / 100);
                  return (
                    <details className="settings-card__proration-dd">
                      <summary className="settings-card__proration-summary">
                        <span className="settings-card__proration-label">Revised price</span>
                        <span className="settings-card__proration-amount">
                          {formatMoney(dueToday, preview.currency)}
                        </span>
                        <svg className="settings-card__proration-caret" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                          <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </summary>
                      <div className="settings-card__proration">
                        <div className="billing-kv">
                          <span className="billing-kv__label">Credit from current plan</span>
                          <span className="billing-kv__value">−{formatMoney(preview.credit, preview.currency)}</span>
                        </div>
                        {disc && couponDiscount > 0 && (
                          <div className="billing-kv">
                            <span className="billing-kv__label">Coupon ({appliedCode})</span>
                            <span className="billing-kv__value">−{formatMoney(couponDiscount, preview.currency)}</span>
                          </div>
                        )}
                        <div className="billing-kv billing-kv--due">
                          <span className="billing-kv__label">Amount due today</span>
                          <span className="billing-kv__value">{formatMoney(dueToday, preview.currency)}</span>
                        </div>
                      </div>
                    </details>
                  );
                })()}

                <div className="settings-card__actions">
                  {relation === 'current' ? (
                    <button type="button" className="settings-btn" disabled>
                      {scheduledDowngrade ? 'Current (until period ends)' : 'Current'}
                    </button>
                  ) : relation === 'upgrade' ? (
                    <button
                      type="button"
                      className="settings-btn settings-btn--primary"
                      disabled={busy || busyAny}
                      onClick={() => setUpgradePlan(plan)}
                    >
                      Upgrade now
                    </button>
                  ) : relation === 'downgrade-free' ? (
                    confirmDowngrade?.id === plan.id ? (
                      <div className="billing-actions">
                        <button
                          type="button"
                          className="settings-btn settings-btn--primary"
                          disabled={busyAny}
                          onClick={() => void scheduleDowngrade(plan)}
                        >
                          {acting ? 'Scheduling…' : 'Confirm downgrade'}
                        </button>
                        <button
                          type="button"
                          className="settings-btn"
                          disabled={busyAny}
                          onClick={() => setConfirmDowngrade(null)}
                        >
                          Keep plan
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="settings-btn"
                        disabled={busyAny || scheduledDowngrade != null}
                        onClick={() => setConfirmDowngrade(plan)}
                        title={`Starts when your current plan ends (${formatDate(periodEnd)})`}
                      >
                        Schedule downgrade
                      </button>
                    )
                  ) : relation === 'downgrade-paid' ? (
                    <button
                      type="button"
                      className="settings-btn"
                      disabled
                      title="To move to a lower paid plan, downgrade to Free first or wait until your current period ends."
                    >
                      Unavailable
                    </button>
                  ) : (
                    <button type="button" className="settings-btn" disabled>
                      Unavailable
                    </button>
                  )}
                </div>

                {relation === 'downgrade-free' && confirmDowngrade?.id === plan.id && (
                  <p className="settings-muted settings-card__note">
                    Your current plan stays active until <strong>{formatDate(periodEnd)}</strong>. {plan.name} starts
                    automatically after that.
                  </p>
                )}
              </div>
            );
        };
        return (
          <>
            {generalPlans.length > 0 && (
              <>
                {bothGroups && groupTitle('General · chat-first (more Assist)')}
                <div className="settings-grid">{generalPlans.map(renderPlanCard)}</div>
              </>
            )}
            {developerPlans.length > 0 && (
              <>
                {bothGroups && groupTitle('Developer · coding-first (more BrewwCode)')}
                <div className="settings-grid">{developerPlans.map(renderPlanCard)}</div>
              </>
            )}
          </>
        );
      })()}

      {!loading && !error && plans.length === 0 && (
        <p className="settings-muted">No plans are available right now.</p>
      )}

      <div className="settings-policies-disclaimer">
        All subscriptions, payments, and invoices are governed by our{' '}
        <a href={getDocsUrl('/docs/pricing-terms')} target="_blank" rel="noopener noreferrer">
          Pricing Terms & Conditions
        </a>{' '}
        and{' '}
        <a href={getDocsUrl('/docs/refund-policy')} target="_blank" rel="noopener noreferrer">
          Refund Policy
        </a>
        . For further details on payment security and billing practices, please read our{' '}
        <a href={getDocsUrl('/docs/terms-of-service')} target="_blank" rel="noopener noreferrer">
          Terms of Service
        </a>{' '}
        or contact{' '}
        <a href={getDocsUrl('/docs/help-support')} target="_blank" rel="noopener noreferrer">
          Help & Support
        </a>
        .
      </div>

      <UpgradeModal
        plan={upgradePlan}
        coupon={upgradeCoupon}
        busy={busyAny}
        onClose={() => setUpgradePlan(null)}
        onPayNow={(plan) => {
          // Close the modal and hand off to checkout in the SAME click.
          setUpgradePlan(null);
          void startCheckout(plan);
        }}
      />
    </div>
  );
}
